/**
 * StatCard Component
 * 관리자 대시보드 통계 카드
 */
import React from 'react';
import styled from 'styled-components';
import { tokens } from '../../design-system/tokens';

interface StatCardProps {
  title: string;
  value: number | string;
  icon?: string;
  unit?: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
}

export const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  unit = '',
  trend
}) => {
  return (
    <Card>
      <Header>
        <Title>{title}</Title>
        {icon && <Icon>{icon}</Icon>}
      </Header>

      <Value>
        {typeof value === 'number' ? value.toLocaleString() : value}
        {unit && <Unit>{unit}</Unit>}
      </Value>

      {trend && (
        <Trend $isPositive={trend.isPositive}>
          <TrendArrow>{trend.isPositive ? '↑' : '↓'}</TrendArrow>
          <TrendValue>{Math.abs(trend.value)}%</TrendValue>
        </Trend>
      )}
    </Card>
  );
};

const Card = styled.div`
  background: ${tokens.colors.neutral[0]};
  border: 1px solid ${tokens.colors.neutral[200]};
  border-radius: ${tokens.borderRadius.lg};
  padding: ${tokens.spacing[6]};
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacing[3]};
  transition: all ${tokens.animation.duration.base} ${tokens.animation.easing.easeOut};

  &:hover {
    box-shadow: ${tokens.shadows.md};
    transform: translateY(-2px);
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h3`
  font-size: ${tokens.typography.fontSize.sm};
  font-weight: ${tokens.typography.fontWeight.medium};
  color: ${tokens.colors.neutral[600]};
  margin: 0;
`;

const Icon = styled.span`
  font-size: ${tokens.typography.fontSize['2xl']};
`;

const Value = styled.div`
  font-size: ${tokens.typography.fontSize['3xl']};
  font-weight: ${tokens.typography.fontWeight.bold};
  color: ${tokens.colors.neutral[800]};
  display: flex;
  align-items: baseline;
  gap: ${tokens.spacing[2]};
`;

const Unit = styled.span`
  font-size: ${tokens.typography.fontSize.base};
  font-weight: ${tokens.typography.fontWeight.regular};
  color: ${tokens.colors.neutral[500]};
`;

const Trend = styled.div<{ $isPositive: boolean }>`
  display: flex;
  align-items: center;
  gap: ${tokens.spacing[1]};
  font-size: ${tokens.typography.fontSize.sm};
  color: ${props => props.$isPositive ? tokens.colors.semantic.success : tokens.colors.semantic.error};
`;

const TrendArrow = styled.span`
  font-weight: ${tokens.typography.fontWeight.bold};
`;

const TrendValue = styled.span`
  font-weight: ${tokens.typography.fontWeight.medium};
`;
